import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, Table, TableHead, TableRow, TableCell, TableBody,
  Button, Chip, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, IconButton, Tooltip, Alert, Stack, LinearProgress
} from '@mui/material';
import { Add, Edit, Subscriptions, Cancel } from '@mui/icons-material';
import api from '../../services/api';

const STATUS_COLOR: any = { active:'success', suspended:'warning', cancelled:'error' };
const CURRENCIES = ['AED','USD'];

const fmtDate = (d: any) => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—';

export const SubscriptionsPage: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [tenants, setTenants] = useState<any[]>([]);
  const [skus, setSkus] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterTenant, setFilterTenant] = useState('');
  const [dialog, setDialog] = useState<{ data?: any } | null>(null);
  const [form, setForm] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = filterTenant ? `?tenant_id=${filterTenant}` : '';
      const { data: res } = await api.get(`/subscriptions/customer${p}`);
      setData(res);
    } finally { setLoading(false); }
  }, [filterTenant]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/tenants?limit=200&tier=customer').then(r => setTenants(r.data.data || []));
    api.get('/subscriptions/skus').then(r => setSkus(r.data));
  }, []);

  const openCreate = () => {
    setForm({ currency: 'AED', quantity: 1, billing_day: 1, start_date: new Date().toISOString().slice(0,10) });
    setError(''); setDialog({});
  };
  const openEdit = (row: any) => {
    setForm({ ...row, start_date: row.start_date?.slice(0,10), end_date: row.end_date?.slice(0,10) });
    setError(''); setDialog({ data: row });
  };

  const save = async () => {
    setSaving(true); setError('');
    try {
      if (dialog?.data) {
        await api.patch(`/subscriptions/customer/${dialog.data.id}`, form);
      } else {
        await api.post('/subscriptions/customer', form);
      }
      setDialog(null); load();
    } catch (e: any) { setError(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const cancelSub = async (id: string) => {
    if (!window.confirm('Cancel this subscription?')) return;
    await api.patch(`/subscriptions/customer/${id}`, { status: 'cancelled' });
    load();
  };

  const selectedSku = skus.find(s => s.id === form.sku_id);

  // Group by tenant for display
  const byTenant: Record<string, any[]> = {};
  data.forEach(r => {
    if (!byTenant[r.tenant_id]) byTenant[r.tenant_id] = [];
    byTenant[r.tenant_id].push(r);
  });

  return (
    <Box>
      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:3 }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
          <Subscriptions sx={{ color:'primary.main', fontSize:32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Customer Subscriptions</Typography>
            <Typography variant="body2" color="text.secondary">{data.length} active subscriptions</Typography>
          </Box>
        </Box>
        <Box sx={{ display:'flex', gap:1 }}>
          <TextField select size="small" label="Filter by Customer" value={filterTenant}
            onChange={e => setFilterTenant(e.target.value)} sx={{ minWidth:200 }}>
            <MenuItem value=""><em>All Customers</em></MenuItem>
            {tenants.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
          </TextField>
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Add Subscription</Button>
        </Box>
      </Box>

      {loading ? <Box sx={{ p:6, textAlign:'center' }}><CircularProgress /></Box> : (
        <Stack spacing={3}>
          {Object.entries(byTenant).map(([tenantId, subs]) => (
            <Card key={tenantId}>
              <Box sx={{ p:2, pb:0 }}>
                <Typography variant="subtitle1" fontWeight={700}>{subs[0].tenant_name}</Typography>
              </Box>
              <Table size="small">
                <TableHead><TableRow>
                  {['Product','SKU','Dest.','Price','Currency','AED Equiv.','Start Date','Status','Actions'].map(h =>
                    <TableCell key={h} sx={{ fontWeight:700, whiteSpace:'nowrap' }}>{h}</TableCell>)}
                </TableRow></TableHead>
                <TableBody>
                  {subs.map(row => (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{row.product_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{row.category_name}</Typography>
                      </TableCell>
                      <TableCell>{row.sku_name}</TableCell>
                      <TableCell>{row.destination ? <Chip size="small" label={row.destination} /> : '—'}</TableCell>
                      <TableCell sx={{ fontFamily:'monospace', fontWeight:600 }}>
                        {parseFloat(row.price).toFixed(2)}
                      </TableCell>
                      <TableCell><Chip size="small" label={row.currency} color={row.currency==='USD'?'success':'primary'} /></TableCell>
                      <TableCell sx={{ fontFamily:'monospace' }}>
                        {parseFloat(row.price_aed).toFixed(2)} <Typography component="span" variant="caption" color="text.secondary">AED</Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize:12, color:'text.secondary' }}>{fmtDate(row.start_date)}</TableCell>
                      <TableCell><Chip size="small" label={row.status} color={STATUS_COLOR[row.status]||'default'} /></TableCell>
                      <TableCell sx={{ whiteSpace:'nowrap' }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(row)}><Edit fontSize="small" /></IconButton>
                        </Tooltip>
                        {row.status === 'active' && (
                          <Tooltip title="Cancel Subscription">
                            <IconButton size="small" color="error" onClick={() => cancelSub(row.id)}><Cancel fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={5} />
                    <TableCell sx={{ fontFamily:'monospace', fontWeight:700, borderTop:'2px solid', borderColor:'divider' }}>
                      {subs.filter(s=>s.status==='active').reduce((sum,s)=>sum+parseFloat(s.price_aed),0).toFixed(2)} AED/mo
                    </TableCell>
                    <TableCell colSpan={3} />
                  </TableRow>
                </TableBody>
              </Table>
            </Card>
          ))}
          {data.length === 0 && (
            <Card sx={{ p:6, textAlign:'center' }}>
              <Typography color="text.secondary">No subscriptions yet. Click "Add Subscription" to get started.</Typography>
            </Card>
          )}
        </Stack>
      )}

      {/* ── Add / Edit Dialog ── */}
      <Dialog open={!!dialog} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{dialog?.data ? 'Edit Subscription' : 'New Subscription'}</DialogTitle>
        <DialogContent sx={{ pt:2 }}>
          {error && <Alert severity="error" sx={{ mb:2 }}>{error}</Alert>}
          <Stack spacing={2} sx={{ mt:1 }}>
            {!dialog?.data && (<>
              <TextField select fullWidth size="small" label="Customer *" value={form.tenant_id||''}
                onChange={e => setForm({...form, tenant_id:e.target.value})}>
                <MenuItem value=""><em>Select customer…</em></MenuItem>
                {tenants.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </TextField>
              <TextField select fullWidth size="small" label="Product / SKU *" value={form.sku_id||''}
                onChange={e => setForm({...form, sku_id:e.target.value, currency: skus.find(s=>s.id===e.target.value)?.currency || 'AED', price: skus.find(s=>s.id===e.target.value)?.default_price || ''})}>
                <MenuItem value=""><em>Select product…</em></MenuItem>
                {skus.filter(s=>s.is_active).map(s => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.category_name} › {s.product_name} — {s.name}
                    {s.bundle_minutes ? ` (${s.bundle_minutes.toLocaleString()} min)` : ''}
                    {s.destination ? ` [${s.destination}]` : ''}
                  </MenuItem>
                ))}
              </TextField>
            </>)}

            {selectedSku && !dialog?.data && (
              <Alert severity="info" sx={{ py:0.5 }}>
                Default: {selectedSku.default_price} {selectedSku.currency}
                {selectedSku.bundle_minutes ? ` · ${selectedSku.bundle_minutes.toLocaleString()} min bundle` : ''}
                {parseFloat(selectedSku.overage_rate) > 0 ? ` · Overage: ${selectedSku.overage_rate}/${selectedSku.currency}/min` : ''}
              </Alert>
            )}

            <Box sx={{ display:'flex', gap:1 }}>
              <TextField fullWidth size="small" type="number" label="Price *" value={form.price||''}
                onChange={e => setForm({...form, price:e.target.value})} inputProps={{ step:'0.01' }} />
              <TextField select size="small" label="Currency" value={form.currency||'AED'}
                onChange={e => setForm({...form, currency:e.target.value})} sx={{ minWidth:100 }}>
                {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Box>

            {(selectedSku?.product_type === 'bundle' || dialog?.data?.product_type === 'bundle') && (
              <TextField fullWidth size="small" label="ASTPP Account (for future CDR pull)" value={form.astpp_account||''}
                onChange={e => setForm({...form, astpp_account:e.target.value})}
                helperText="Enter the account name in ASTPP to auto-pull CDRs after migration" />
            )}

            <TextField fullWidth size="small" type="number" label="Quantity" value={form.quantity||1}
              onChange={e => setForm({...form, quantity:parseInt(e.target.value)||1})}
              helperText="For per-seat / per-agent products" />
            <TextField fullWidth size="small" type="date" label="Start Date *" value={form.start_date||''}
              onChange={e => setForm({...form, start_date:e.target.value})} InputLabelProps={{ shrink:true }} />
            <TextField fullWidth size="small" type="date" label="End Date (leave blank = ongoing)" value={form.end_date||''}
              onChange={e => setForm({...form, end_date:e.target.value})} InputLabelProps={{ shrink:true }} />

            {dialog?.data && (
              <TextField select fullWidth size="small" label="Status" value={form.status||'active'}
                onChange={e => setForm({...form, status:e.target.value})}>
                {['active','suspended','cancelled'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            )}
            <TextField fullWidth size="small" label="Notes" multiline rows={2} value={form.notes||''}
              onChange={e => setForm({...form, notes:e.target.value})} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p:2 }}>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>{saving ? <CircularProgress size={20} /> : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
